import { useEffect, useId, useRef, useState } from 'react';
import { Mic, Square } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageProvider';
import { useFitnessExerciseNotes } from '@/hooks/useFitnessExerciseNotes';
import { useVoiceInputWhisper, type WhisperOwnerGuard } from '@/hooks/useVoiceInputWhisper';
import { FITNESS_EXERCISE_NOTE_LIMIT, fitnessExerciseNoteKey } from '@/lib/fitness/fitnessPreferences';
import { getFitnessPreferencesCopy } from '@/lib/fitnessPreferencesCopy';

interface Props { ownerId: string; exerciseName: string; muscle: string; disabled?: boolean; onSaved?: (notes: string) => void }
function NotesEditor({ ownerId, exerciseName, muscle, disabled = false, onSaved }: Props) {
  const { lang, t } = useLanguage(), copy = getFitnessPreferencesCopy(lang);
  const note = useFitnessExerciseNotes(ownerId, exerciseName, muscle);
  const voice = useVoiceInputWhisper(lang, { surface: 'work' });
  const cancelVoice = voice.cancelListening;
  const inputId = useId(), [draft, setDraft] = useState(''), [saved, setSaved] = useState(false), [voiceError, setVoiceError] = useState(false);
  const initialized = useRef(false), baseline = useRef(''), mounted = useRef(true), voiceGuard = useRef<WhisperOwnerGuard | null>(null);
  const enabled = !disabled && !note.busy && note.status === 'ready';
  const enabledRef = useRef(enabled); enabledRef.current = enabled;
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    if (note.status !== 'ready') return;
    if (!initialized.current || draft === baseline.current) {
      initialized.current = true; baseline.current = note.notes; setDraft(note.notes);
    }
  }, [note.notes, note.status, draft]);
  useEffect(() => { if (!enabled) { cancelVoice(); voiceGuard.current = null; } }, [enabled, cancelVoice]);
  const dictate = async () => {
    setVoiceError(false);
    if (voice.isListening) {
      const guard = voiceGuard.current;
      if (!guard) return;
      const text = await voice.stopListening(guard);
      if (!mounted.current || !guard.isCurrent()) return;
      if (text) { setDraft(previous => (previous + (previous ? ' ' : '') + text).slice(0, FITNESS_EXERCISE_NOTE_LIMIT)); setSaved(false); }
      else setVoiceError(true);
      voiceGuard.current = null; return;
    }
    if (!enabled) return;
    const ownerCurrent = note.capture();
    const guard: WhisperOwnerGuard = { ownerId, isCurrent: () => mounted.current && enabledRef.current && ownerCurrent() };
    voiceGuard.current = guard;
    try { if (!await voice.startListening(guard) && guard.isCurrent()) setVoiceError(true); }
    catch { if (guard.isCurrent()) { console.error('[Fitness] note dictation failed'); setVoiceError(true); } }
  };
  const save = async () => {
    if (!enabled || voice.isListening || voice.isTranscribing) return;
    const current = note.capture(), value = draft;
    if (await note.saveNotes(value) && mounted.current && current()) {
      baseline.current = value; setSaved(true); onSaved?.(value);
    }
  };
  return <details className="ph-no-capture rounded-2xl border border-border bg-card px-4">
    <summary className="flex min-h-11 cursor-pointer items-center text-sm font-semibold">{copy.notes}</summary>
    <div className="pb-4 pt-1">
    <label htmlFor={inputId} className="sr-only">{copy.notes}</label>
    <p className="mb-3 mt-1 text-xs text-muted-foreground">{copy.notesHint}</p>
    {note.status === 'loading' && <p role="status" className="text-sm text-muted-foreground">{t('common.loading')}</p>}
    <textarea id={inputId} data-private="true" data-ph-no-capture="true" value={draft} disabled={!enabled || voice.isListening || voice.isTranscribing}
      maxLength={FITNESS_EXERCISE_NOTE_LIMIT} placeholder={copy.notesPlaceholder}
      onChange={event => { setDraft(event.target.value); setSaved(false); }}
      className="min-h-24 w-full resize-y rounded-xl border border-border bg-background px-3 py-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50" />
    <div className="mt-2 flex flex-wrap items-center gap-2">
      {voice.supported && <button type="button" disabled={!enabled || voice.isTranscribing} onClick={() => void dictate()}
        aria-label={voice.isListening ? copy.stopDictation : copy.dictate} aria-pressed={voice.isListening}
        className="flex min-h-11 items-center gap-2 rounded-xl border border-border px-3 text-sm disabled:opacity-40">
        {voice.isListening ? <Square size={16} /> : <Mic size={16} />}{voice.isListening ? copy.stopDictation : copy.dictate}
      </button>}
      <button type="button" disabled={!enabled || voice.isListening || voice.isTranscribing || draft === note.notes}
        onClick={() => void save()} className="min-h-11 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground disabled:opacity-40">{note.busy ? copy.saving : copy.saveNotes}</button>
      <button type="button" disabled={!enabled || !draft || voice.isListening || voice.isTranscribing}
        onClick={() => { setDraft(''); setSaved(false); }} className="min-h-11 rounded-xl px-3 text-sm text-muted-foreground disabled:opacity-40">{copy.clear}</button>
      <span className="ml-auto text-xs tabular-nums text-muted-foreground">{draft.length}/{FITNESS_EXERCISE_NOTE_LIMIT}</span>
    </div>
    {voice.isTranscribing && <p role="status" className="mt-2 text-sm text-muted-foreground">{copy.transcribing}</p>}
    {voiceError && <p role="alert" className="mt-2 text-sm text-destructive">{copy.voiceError}</p>}
    {note.error && <div role="alert" className="mt-2 text-sm text-destructive">{copy.saveError}
      <button type="button" onClick={() => void note.retry()} className="ml-2 min-h-11 text-primary">{copy.retry}</button>
    </div>}
    {saved && !note.error && <p role="status" className="mt-2 text-sm text-primary">{copy.saved}</p>}
    </div>
  </details>;
}
export default function FitnessExerciseNotes(props: Props) {
  return <NotesEditor key={JSON.stringify([props.ownerId, fitnessExerciseNoteKey(props.exerciseName, props.muscle)])} {...props} />;
}
